import { FirebaseAuthentication, ProviderId } from "@capacitor-firebase/authentication";
import { Capacitor } from "@capacitor/core";
import { IonSpinner } from "@ionic/react";
import { serverUrl } from "Requests/Api";
import { message } from "antd";
import { getApp } from "firebase/app";
import {
    GoogleAuthProvider,
    getAuth,
    indexedDBLocalPersistence,
    initializeAuth,
    signInWithCredential,
    OAuthProvider,
} from "firebase/auth";
import { initializeAppWithConfig } from "firebaseHelpers";
import SignInWithApple from "frontend-lib/Components/SignInWithApple";
import SignInWithGoogle from "frontend-lib/Components/SignInWithGoogle";
import { Logger } from "frontend-lib/Log";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type AccessToken = string;
interface FirebaseSignInProps {
    googleText?: string;
    onAuthCb(accessToken: string): void;
    onLoadingCb?(loading: boolean): void;
}

const getFirebaseAuth = async () => {
    initializeAppWithConfig();
    if (Capacitor.isNativePlatform()) {
        return initializeAuth(getApp(), {
            persistence: indexedDBLocalPersistence,
        });
    } else {
        return getAuth();
    }
};

export default function FirebaseAuth({ googleText, onAuthCb, onLoadingCb }: FirebaseSignInProps) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const logger = useMemo(() => new Logger("Agent/FirebaseAuth.tsx", serverUrl), []);

    useEffect(() => {
        if (onLoadingCb) {
            onLoadingCb(isLoading);
        }
    }, [isLoading, onLoadingCb]);

    useEffect(() => {
        getFirebaseAuth();
    }, []);

    const signInWithGoogle = async (): Promise<AccessToken> => {
        // 1. Create credentials on the native layer
        const result = await FirebaseAuthentication.signInWithGoogle();
        // 2. Sign in on the web layer using the id token
        const credential = GoogleAuthProvider.credential(result.credential?.idToken);
        const auth = await getFirebaseAuth();
        const resSignIn = await signInWithCredential(auth, credential);
        return await resSignIn.user.getIdToken();
    };

    const signInWithApple = async (): Promise<AccessToken> => {
        // 1. Create credentials on the native layer
        const result = await FirebaseAuthentication.signInWithApple({
            skipNativeAuth: true,
        });
        // 2. Sign in on the web layer using the id token and nonce
        const provider = new OAuthProvider("apple.com");
        const credential = provider.credential({
            idToken: result.credential?.idToken,
            rawNonce: result.credential?.nonce,
        });
        const auth = getAuth();
        const resSignIn = await signInWithCredential(auth, credential);
        return resSignIn.user.getIdToken();
    };

    const onClickSignUp = async (provider: ProviderId) => {
        try {
            let token: string | undefined;
            setIsLoading(true);
            switch (provider) {
                case ProviderId.GOOGLE:
                    token = await signInWithGoogle();
                    break;
                case ProviderId.APPLE:
                    token = await signInWithApple();
                    break;
                default:
                    throw new Error("Not implemented");
            }
            onAuthCb(token!);
        } catch (e: any) {
            if (e.errorMessage) {
                const errMsg = e.errorMessage;
                const errorMessages = [
                    "(com.apple.AuthenticationServices.AuthorizationError error 1001.)",
                    "The user canceled the sign-in flow.",
                ];
                for (const msg of errorMessages) {
                    if (errMsg.includes(msg)) return;
                }
            }
            logger.error(`Error in sign in with firebase. Error: ${e}, json: ${JSON.stringify(e)}`);
            message.error(t("unknownError"));
        } finally {
            setIsLoading(false);
        }
    };

    const loadingSpinnerWrapper = (children: JSX.Element) => {
        if (isLoading) {
            return <IonSpinner>{children}</IonSpinner>;
        } else {
            return <>{children}</>;
        }
    };

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                padding: "5px",
            }}
        >
            {loadingSpinnerWrapper(
                <>
                    <SignInWithGoogle onClick={() => onClickSignUp(ProviderId.GOOGLE)} />
                    <div style={{ minHeight: "5pt" }} />
                    <SignInWithApple onClickCb={() => onClickSignUp(ProviderId.APPLE)} />
                    {/* <div style={{ minHeight: "5pt" }} />
                    <SignInWithFb onClickCb={async () => alert("Not implemented")} /> */}
                </>
            )}
        </div>
    );
}
